import React, { useState, useEffect } from "react";
import { Form, Input, Button, Typography, Checkbox, message } from "antd";
import { MailOutlined, LockOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { authService } from "../../../services/authService";
import { all_routes } from "../../../routes/all_routes";
import loginScreen from "../../../images/loginscreen.svg";

const { Title, Text } = Typography;

const Login = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [rememberMe, setRememberMe] = useState(false);

  // Load saved email on component mount
  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    if (savedEmail) {
      form.setFieldsValue({ email: savedEmail });
      setRememberMe(true);
    }
  }, [form]);

  const onFinish = async ({ email, password }) => {
    try {
      setLoading(true);
      const response = await authService.login(email, password);

      if (response.status) {
        // Handle remember me
        if (rememberMe) {
          localStorage.setItem("rememberedEmail", email);
        } else {
          localStorage.removeItem("rememberedEmail");
        }

        message.success("Login successful!");
        const role = response.data.user.role;

        if (role === "admin" || role === "super_admin") {
          navigate(all_routes.adminDashboard);
        } else {
          navigate(all_routes.userDashboard);
        }
      } else {
        message.error(response.message || "Login failed!");
      }
    } catch (err) {
      message.error(err.message || "Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* LEFT SECTION */}
      <div className="hidden lg:flex w-1/2 bg-gradient-to-br from-indigo-600 text-white p-12 relative">
        <div className="max-w-lg">
          <Title level={2} className="text-white mb-4">
            Welcome back!
          </Title>

          <Text className="text-indigo-100 text-lg">
            Please sign in to your <b>Capture testimonial</b> account
          </Text>

          <div className="mt-6 bg-white/10 rounded-xl p-6">
            <Text className="text-indigo-100">
              We're excited to hear about your experience with our
              product/service. Your testimonial will help others make informed
              decisions.
            </Text>
          </div>

          <img
            src={loginScreen}
            alt="Login screen illustration"
            className="mt-10 w-full max-w-md"
          />
        </div>
      </div>

      {/* RIGHT SECTION */}
      <div className="flex w-full lg:w-1/2 items-center justify-center bg-gray-50 px-6">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-6">
              <Title level={3} className="mb-1">
                Sign In
              </Title>
              <Text type="secondary">
                Welcome back! Please enter your details
              </Text>
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              initialValues={{
                remember: false,
              }}
            >
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: "Please enter your email" },
                  { type: "email", message: "Invalid email address" },
                ]}
              >
                <Input
                  size="large"
                  prefix={<MailOutlined />}
                  placeholder="example@gmail.com"
                  className="rounded-lg"
                />
              </Form.Item>

              <Form.Item
                name="password"
                label="Password"
                rules={[
                  { required: true, message: "Please enter your password" },
                ]}
              >
                <Input.Password
                  size="large"
                  prefix={<LockOutlined />}
                  placeholder="Enter your password"
                  className="rounded-lg"
                />
              </Form.Item>

              <div className="flex justify-between items-center mb-4">
                <Form.Item name="remember" valuePropName="checked" noStyle>
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  >
                    Remember me
                  </Checkbox>
                </Form.Item>
                {/* <Text className="text-indigo-600 cursor-pointer hover:underline">
                  Forgot password?
                </Text> */}
              </div>

              <Button
                htmlType="submit"
                loading={loading}
                size="large"
                className="w-full h-12 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold border-0"
              >
                Sign In →
              </Button>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
